/**
 * Lumi analytics — one module for the whole store.
 *
 * Replaces the old theme's three duplicated PostHog snippets (theme.liquid,
 * landing.liquid, terra-posthog.liquid) and its two competing
 * __terraInjectSession definitions. Everything lives here and is loaded once
 * from layout/theme.liquid, so every page and every funnel is covered.
 *
 * What it does:
 *   1. Attribution — first- and last-touch UTMs / click ids, kept in storage.
 *   2. Cart attributes — stamps the PostHog session id + attribution onto the
 *      Shopify cart, which is how the dashboard joins orders back to session
 *      recordings (order.note_attributes.terra_session_id).
 *   3. Commerce events — add_to_cart / add_to_cart_enriched / initiate_checkout,
 *      captured by intercepting the cart AJAX routes so every funnel (picker,
 *      PDP, buy-box, drawer upsells, quiz) is instrumented with no per-funnel code.
 *   4. Quiz + form events — quiz_answer, form_field_focus, email_submitted,
 *      quiz_completed, plus identify() so recordings carry the person's email.
 *
 * Event names and property names are the ApClaw dashboard's contract — do not
 * rename without updating apclaw-dashboard/api/posthog.js.
 *
 * PostHog itself is loaded by snippets/lumi-analytics.liquid before this file.
 */
(function () {
  'use strict';

  var CART_ATTR_FLAG = 'lumi_attr_stamped';
  var ATTR_KEY = 'lumi_attr';
  var FIRST_KEY = 'lumi_attr_first';

  function ph() {
    return window.posthog && typeof window.posthog.capture === 'function' ? window.posthog : null;
  }

  function capture(event, props) {
    var p = ph();
    if (!p) return;
    try {
      p.capture(event, props || {});
    } catch (e) {
      /* never let analytics break the page */
    }
  }

  function readJSON(store, key) {
    try {
      var raw = store.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeJSON(store, key, value) {
    try {
      store.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* private mode / quota — attribution degrades, nothing breaks */
    }
  }

  // ---------------------------------------------------------------------------
  // 1. Attribution
  // ---------------------------------------------------------------------------

  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'];
  var CLICK_KEYS = ['fbclid', 'gclid', 'ttclid', 'ad_id', 'campaign_id'];

  function currentAttribution() {
    var params = new URLSearchParams(window.location.search);
    var found = {};
    var hasAny = false;

    UTM_KEYS.concat(CLICK_KEYS).forEach(function (key) {
      var value = params.get(key);
      if (value) {
        found[key] = value;
        hasAny = true;
      }
    });

    // Last-touch: only overwrite when this pageview actually carries UTMs, so
    // internal navigation doesn't wipe the campaign that brought them in.
    var stored = readJSON(sessionStorage, ATTR_KEY) || {};
    if (hasAny) {
      found.landing_site = window.location.href;
      writeJSON(sessionStorage, ATTR_KEY, found);
      stored = found;
    }

    // First-touch: written once, ever.
    if (!readJSON(localStorage, FIRST_KEY)) {
      writeJSON(localStorage, FIRST_KEY, {
        first_utm_source: stored.utm_source || 'direct',
        first_utm_campaign: stored.utm_campaign || '',
        first_utm_content: stored.utm_content || '',
        first_landing: window.location.pathname,
        first_landing_site: window.location.href,
        first_referrer: document.referrer || ''
      });
    }

    return stored;
  }

  function attributionPayload() {
    var last = readJSON(sessionStorage, ATTR_KEY) || {};
    var first = readJSON(localStorage, FIRST_KEY) || {};
    var p = ph();
    var attrs = {};

    // The join key the dashboard relies on: PostHog session id → order.
    if (p && typeof p.get_session_id === 'function') {
      var sid = p.get_session_id();
      if (sid) attrs.terra_session_id = sid;
    }
    if (p && typeof p.get_distinct_id === 'function') {
      var did = p.get_distinct_id();
      if (did) attrs.terra_cid = did;
    }

    UTM_KEYS.forEach(function (key) {
      if (last[key]) attrs[key] = last[key];
    });
    if (last.fbclid) attrs.terra_fbclid = last.fbclid;
    if (last.landing_site) attrs.landing_site = last.landing_site;

    ['first_utm_source', 'first_utm_campaign', 'first_utm_content', 'first_landing', 'first_landing_site'].forEach(
      function (key) {
        if (first[key]) attrs[key] = first[key];
      }
    );

    return attrs;
  }

  /**
   * Stamp attribution onto the Shopify cart. `keepalive` so it still lands if
   * the shopper navigates to checkout in the same tick — that is why this
   * never has to block navigation the way the old theme's 1200ms race did.
   * Attributes are written as soon as the cart has contents, well before
   * checkout, and re-asserted on checkout intent.
   */
  function stampCart(force) {
    var attrs = attributionPayload();
    if (!attrs.terra_session_id) return; // PostHog not ready yet — retried by callers
    if (!force) {
      try {
        if (sessionStorage.getItem(CART_ATTR_FLAG) === attrs.terra_session_id) return;
      } catch (e) {
        /* ignore */
      }
    }

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs }),
      keepalive: true
    })
      .then(function () {
        try {
          sessionStorage.setItem(CART_ATTR_FLAG, attrs.terra_session_id);
        } catch (e) {
          /* ignore */
        }
      })
      .catch(function () {
        /* attribution is best-effort; never surface to the shopper */
      });
  }

  // PostHog's session id isn't available synchronously on first paint; retry
  // briefly rather than dropping the stamp for fast add-to-cart clicks.
  function stampCartWhenReady(force) {
    var tries = 0;
    (function attempt() {
      var p = ph();
      if (p && typeof p.get_session_id === 'function' && p.get_session_id()) {
        stampCart(force);
        return;
      }
      if (tries++ < 20) setTimeout(attempt, 150);
    })();
  }

  // ---------------------------------------------------------------------------
  // 2. Commerce events — one interception point for every funnel
  // ---------------------------------------------------------------------------

  function itemsFromAddResponse(data) {
    if (!data) return [];
    if (Array.isArray(data.items)) return data.items; // {items:[…]} bulk form
    if (data.id) return [data]; // single-item form
    return [];
  }

  function captureAddToCart(items) {
    if (!items.length) return;
    var page = window.location.pathname;

    items.forEach(function (item) {
      var title = item.product_title || item.title || '';

      // session-pages.js joins products to sessions off this one.
      capture('add_to_cart', {
        product_title: title,
        page: page,
        variant_id: item.variant_id || item.id,
        price: (item.price || 0) / 100,
        quantity: item.quantity || 1
      });

      // The funnel's "Added to cart" stage counts this one.
      capture('add_to_cart_enriched', {
        product_title: title,
        variant_title: item.variant_title || '',
        price: (item.price || 0) / 100,
        quantity: item.quantity || 1,
        is_subscription: !!(item.selling_plan_allocation || item.selling_plan),
        variant_id: item.variant_id || item.id,
        product_id: item.product_id,
        page: page
      });
    });

    // Cart now has contents worth attributing — stamp before checkout is reachable.
    stampCartWhenReady(false);
  }

  /**
   * Every funnel on this store adds to cart through the AJAX routes (the
   * picker, PDP, buy-box, drawer pair/gift upsells and the quiz all POST to
   * /cart/add.js). Wrapping fetch once instruments all of them, including any
   * funnel built later, with no per-section tracking code.
   */
  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var url = '';
      try {
        url = typeof input === 'string' ? input : input && input.url ? input.url : '';
      } catch (e) {
        url = '';
      }

      var promise = nativeFetch.apply(this, arguments);
      if (url.indexOf('/cart/add') === -1) return promise;

      promise
        .then(function (response) {
          if (!response || !response.ok) return;
          response
            .clone()
            .json()
            .then(function (data) {
              captureAddToCart(itemsFromAddResponse(data));
            })
            .catch(function () {});
        })
        .catch(function () {});

      return promise;
    };
  }

  // Non-AJAX product forms (Dawn's native POST /cart/add) — the response is a
  // navigation, so read intent off the submit instead.
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
      capture('add_to_cart', { product_title: document.title, page: window.location.pathname });
      stampCartWhenReady(false);
    },
    true
  );

  // Checkout intent — the hosted checkout is a different domain PostHog never
  // sees, so this event is the only signal the funnel gets.
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      var btn = target.closest(
        '[name="checkout"], .cart__checkout-button, #CartDrawer-Checkout, a[href*="/checkout"]'
      );
      if (!btn) return;

      capture('initiate_checkout', {
        page: window.location.pathname,
        source: btn.id || btn.className || 'checkout-button'
      });
      stampCart(true); // keepalive — survives the navigation
    },
    true
  );

  // ---------------------------------------------------------------------------
  // 3. Forms + quiz
  // ---------------------------------------------------------------------------

  // Powers the dashboard's Form Field & Quiz Funnel tab.
  document.addEventListener(
    'focusin',
    function (event) {
      var el = event.target;
      if (!el || !el.tagName) return;
      var tag = el.tagName.toLowerCase();
      if (tag !== 'input' && tag !== 'textarea' && tag !== 'select') return;
      if (el.type === 'hidden' || el.type === 'submit') return;

      capture('form_field_focus', {
        field_name: el.name || el.id || el.type || 'unknown',
        field_type: el.type || tag,
        form_id: (el.form && (el.form.id || el.form.className)) || '',
        page: window.location.pathname
      });
    },
    true
  );

  // Newsletter (footer) — the quiz reports its own submit via lumiAnalytics.
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.classList) return;
      if (!form.matches('form[action*="/contact"], .newsletter-form, .footer__newsletter form')) return;
      var field = form.querySelector('input[type="email"]');
      if (!field || !field.value) return;

      capture('email_submitted', { placement: 'newsletter', page: window.location.pathname });
      identify(field.value, { email_source: 'newsletter' });
    },
    true
  );

  function identify(email, props) {
    var p = ph();
    if (!p || !email || typeof p.identify !== 'function') return;
    try {
      var payload = { email: email };
      Object.keys(props || {}).forEach(function (k) {
        payload[k] = props[k];
      });
      // Identifying by email is what puts a name on the session recording and
      // makes the person searchable in the dashboard.
      p.identify(email, payload);
    } catch (e) {
      /* ignore */
    }
  }

  // ---------------------------------------------------------------------------
  // Public surface — used by the quiz popup (assets/lumi-popup.js)
  // ---------------------------------------------------------------------------
  window.lumiAnalytics = {
    capture: capture,
    identify: identify,
    stampCart: stampCartWhenReady,
    attribution: attributionPayload
  };

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------
  currentAttribution();

  // Returning shoppers can land straight on a filled cart; make sure it carries
  // this session's id even if they never re-add anything.
  fetch('/cart.js', { headers: { Accept: 'application/json' } })
    .then(function (r) {
      return r.json();
    })
    .then(function (cart) {
      if (cart && cart.item_count > 0) stampCartWhenReady(false);
    })
    .catch(function () {});
})();
